/**
 * GTM Tracking – Client-Side Event Tracking
 * Handles: Consent, Click-IDs, E-Commerce Events, Engagement, Visitor Identity
 */
(function () {
  'use strict';

  var DL = window.__DL_DATA__ || {};
  var dataLayer = (window.dataLayer = window.dataLayer || []);

  // ─── Helpers ────────────────────────────────────────────────────────

  function pushEvent(name, params) {
    var obj = Object.assign({ event: name }, params || {});
    dataLayer.push(obj);
    document.dispatchEvent(new CustomEvent('dl:' + name, { detail: obj }));
  }

  function clearEcommerce() {
    dataLayer.push({ ecommerce: null });
  }

  function mapCartItemToGA4(item) {
    return {
      item_id: item.sku || String(item.product_id),
      item_name: item.product_title || item.title,
      item_brand: item.vendor,
      item_category: item.product_type || item.type,
      item_variant: item.variant_title,
      price: (item.final_price != null ? item.final_price / 100 : item.price) || 0,
      quantity: item.quantity || 1,
    };
  }

  function getCookie(name) {
    var m = document.cookie.match('(^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[2]) : null;
  }

  function setCookie(name, value, days, sameSite) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    var parts = [
      name + '=' + encodeURIComponent(value),
      'expires=' + d.toUTCString(),
      'path=/',
      'SameSite=' + (sameSite || 'Lax'),
    ];
    if (location.protocol === 'https:') parts.push('Secure');
    document.cookie = parts.join('; ');
  }

  function getParam(name) {
    try {
      return new URL(location.href).searchParams.get(name);
    } catch (e) {
      return null;
    }
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  // ─── A. Consent – Shopify Customer Privacy API ─────────────────────

  function initConsent() {
    // Reagiert auf Consent-Änderungen (Shopify feuert dieses Event nach setTrackingConsent)
    function onConsentChange(detail) {
      var marketing = !!(detail && detail.marketingAllowed);
      // analytics_storage bleibt immer 'granted' – First-Party SST (chri.esel.com)
      if (typeof gtag === 'function') {
        gtag('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: marketing ? 'granted' : 'denied',
          ad_user_data: marketing ? 'granted' : 'denied',
          ad_personalization: marketing ? 'granted' : 'denied',
        });
      }
      pushEvent('consent_update', {
        analytics_allowed: !!(detail && detail.analyticsAllowed),
        marketing_allowed: marketing,
        preferences_allowed: !!(detail && detail.preferencesAllowed),
        sale_of_data_allowed: !!(detail && detail.saleOfDataAllowed),
      });
    }

    // Shopify's natives visitorConsentCollected Event – gefeuert nach setTrackingConsent().
    // Detail-Format: { analyticsAllowed, marketingAllowed, preferencesAllowed, saleOfDataAllowed }
    document.addEventListener('visitorConsentCollected', function (e) {
      onConsentChange(e.detail);
    });

    // Initialen State aus Shopify's *Allowed()-Methoden lesen (Single Source of Truth)
    if (window.Shopify && window.Shopify.customerPrivacy) {
      var cp = window.Shopify.customerPrivacy;
      if (typeof cp.marketingAllowed === 'function') {
        pushEvent('consent_state', {
          analytics_allowed: typeof cp.analyticsProcessingAllowed === 'function' ? cp.analyticsProcessingAllowed() : true,
          marketing_allowed: cp.marketingAllowed(),
          preferences_allowed: typeof cp.preferencesProcessingAllowed === 'function' ? cp.preferencesProcessingAllowed() : true,
          sale_of_data_allowed: typeof cp.saleOfDataAllowed === 'function' ? cp.saleOfDataAllowed() : true,
        });
      }
    }
  }

  // ─── B. Click-ID & Meta Cookie Capture ─────────────────────────────

  function initClickIds() {
    var ids = ['gclid', 'gbraid', 'wbraid', 'fbclid'];
    var data = {};

    ids.forEach(function (id) {
      var val = getParam(id);
      if (val) {
        setCookie('_esel_' + id, val, 90);
        try { sessionStorage.setItem('_esel_' + id, val); } catch (e) {}
        data[id] = val;
      } else {
        data[id] = getCookie('_esel_' + id);
        if (!data[id]) {
          try { data[id] = sessionStorage.getItem('_esel_' + id); } catch (e) {}
        }
      }
    });

    // Meta cookies — generate if not present (replaces browser pixel)
    data._fbp = getCookie('_fbp');
    if (!data._fbp) {
      data._fbp = 'fb.1.' + Date.now() + '.' + Math.floor(Math.random() * 1e16);
      setCookie('_fbp', data._fbp, 90);
    }
    data._fbc = getCookie('_fbc');
    if (!data._fbc && data.fbclid) {
      data._fbc = 'fb.1.' + Date.now() + '.' + data.fbclid;
      setCookie('_fbc', data._fbc, 90);
    }

    // Push non-null values
    var pushData = {};
    Object.keys(data).forEach(function (k) {
      if (data[k]) pushData[k] = data[k];
    });
    if (Object.keys(pushData).length) {
      dataLayer.push(pushData);
    }
  }

  // ─── G. First-Party Visitor Identity ────────────────────────────────

  function initVisitorIdentity() {
    var cookieName = '_esel_vid';
    var raw = getCookie(cookieName);
    var vid;

    try { vid = raw ? JSON.parse(raw) : null; } catch (e) { vid = null; }

    if (!vid || !vid.id) {
      vid = {
        id: crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          var r = (Math.random() * 16) | 0;
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        }),
        first_visit: new Date().toISOString(),
        visit_count: 1,
        ga_client_id: null,
        shopify_y: null,
      };
    } else {
      vid.visit_count = (vid.visit_count || 0) + 1;
    }

    // Extract GA client ID from _ga cookie
    var gaCookie = getCookie('_ga');
    if (gaCookie) {
      var parts = gaCookie.split('.');
      if (parts.length >= 4) {
        vid.ga_client_id = parts[2] + '.' + parts[3];
      }
    }

    // Shopify _shopify_y
    vid.shopify_y = getCookie('_shopify_y') || vid.shopify_y;

    // Save cookie (13 months)
    setCookie(cookieName, JSON.stringify(vid), 395);

    // Push to dataLayer
    dataLayer.push({
      esel_visitor_id: vid.id,
      visit_count: vid.visit_count,
      is_returning_visitor: vid.visit_count > 1,
      ga_client_id: vid.ga_client_id,
      shopify_y: vid.shopify_y,
    });
  }

  // ─── C. Page-Level Events ──────────────────────────────────────────

  function firePageEvents() {
    pushEvent('page_view', {
      page_type: DL.page_type,
      page_title: document.title,
      page_location: location.href,
      page_path: location.pathname,
    });

    // GA4 user properties for logged-in customers
    if (DL.customer_logged_in) {
      dataLayer.push({
        user_id: String(DL.customer_id),
        user_properties: {
          customer_type: { value: DL.customer_type },
          customer_order_count: { value: DL.customer_order_count },
          customer_lifetime_value: { value: DL.customer_total_spent },
        },
      });
    }

    var currency = DL.shop_currency || 'EUR';

    if (DL.product && DL.template === 'product') {
      var p = DL.product;
      clearEcommerce();
      pushEvent('view_item', {
        ecommerce: {
          currency: currency,
          value: p.price,
          items: [
            {
              item_id: p.item_id,
              item_name: p.item_name,
              item_brand: p.item_brand,
              item_category: p.item_category,
              item_variant: p.item_variant,
              price: p.price,
              quantity: 1,
            },
          ],
        },
      });
      trackProductInterest(p);
    }

    if (DL.items && DL.template === 'collection') {
      var items = DL.items.map(function (item, i) {
        return Object.assign({}, item, {
          item_list_name: DL.collection.name,
          item_list_id: DL.collection.handle,
          index: item.index != null ? item.index : i,
        });
      });
      clearEcommerce();
      pushEvent('view_item_list', {
        ecommerce: {
          currency: currency,
          item_list_name: DL.collection.name,
          item_list_id: DL.collection.handle,
          items: items,
        },
      });
    }

    if (DL.items && DL.template === 'search') {
      clearEcommerce();
      pushEvent('view_item_list', {
        ecommerce: {
          currency: currency,
          items: DL.items,
        },
        search_term: DL.search_term,
      });
      pushEvent('search', { search_term: DL.search_term });
    }

    if (DL.cart && DL.template === 'cart') {
      clearEcommerce();
      pushEvent('view_cart', {
        ecommerce: {
          currency: currency,
          value: DL.cart.total,
          items: DL.cart.items,
        },
      });
    }
  }

  // ─── D. Interaction Events ─────────────────────────────────────────

  function initInteractionEvents() {
    var currency = DL.shop_currency || 'EUR';

    // select_item – mousedown on product card (fires before navigation)
    document.addEventListener('mousedown', function (e) {
      var link = e.target.closest('.card--product a');
      if (!link) return;
      var wrapper = link.closest('.card-wrapper');
      if (!wrapper) return;

      var name = wrapper.querySelector('.card__title');
      clearEcommerce();
      pushEvent('select_item', {
        ecommerce: {
          currency: currency,
          items: [
            {
              item_name: name ? name.textContent.trim() : '',
            },
          ],
        },
      });
    });

    // add_to_cart via PubSub
    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event.source !== 'product-form') return;
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var added = cart.items[0]; // most recently added is first
            if (event.productVariantId) {
              var match = cart.items.find(function (i) { return String(i.variant_id) === String(event.productVariantId); });
              if (match) added = match;
            }
            if (!added) return;
            var item = mapCartItemToGA4(added);
            clearEcommerce();
            pushEvent('add_to_cart', {
              ecommerce: {
                currency: currency,
                value: item.price * item.quantity,
                items: [item],
              },
            });
            engagementState.addedToCart = true;
            updateEngagementScore();
          });
      });
    }

    // remove_from_cart
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('cart-remove-button');
      if (!btn) return;
      var index = btn.dataset.index;
      if (!index) return;

      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var lineIndex = parseInt(index, 10) - 1;
          var removed = cart.items[lineIndex];
          if (!removed) return;
          var item = mapCartItemToGA4(removed);
          clearEcommerce();
          pushEvent('remove_from_cart', {
            ecommerce: {
              currency: currency,
              value: item.price * item.quantity,
              items: [item],
            },
          });
        });
    });

    // begin_checkout is handled by the web pixel (checkout_started event)
    // which fires after Shopify actually starts the checkout, with accurate
    // totals and no navigation-blocking setTimeout.
  }

  // ─── E. Variant Change ─────────────────────────────────────────────

  function initVariantChange() {
    if (typeof subscribe !== 'function' || DL.template !== 'product') return;

    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      if (!event || !event.data || !event.data.variant) return;
      var v = event.data.variant;
      if (DL.product) {
        DL.product.item_id = v.sku || String(v.id);
        DL.product.price = v.price / 100;
        DL.product.item_variant = v.title;
        DL.product.variant_id = v.id;
      }
      engagementState.variantChanged = true;
      updateEngagementScore();
    });
  }

  // ─── F. Engagement Events ──────────────────────────────────────────

  var engagementState = {
    scrollThresholds: {},
    timeMilestones: {},
    imagesViewed: {},
    accordionsOpened: {},
    variantChanged: false,
    addedToCart: false,
    score: 0,
  };

  // Scroll Depth (scroll-based, no sentinel divs to avoid layout issues)
  function initScrollDepth() {
    var thresholds = [25, 50, 75, 90];
    var fired = {};

    function checkScroll() {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      var docHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      var winHeight = window.innerHeight;
      var scrollPct = docHeight <= winHeight ? 100 : (scrollTop / (docHeight - winHeight)) * 100;

      thresholds.forEach(function (pct) {
        if (scrollPct >= pct && !fired[pct]) {
          fired[pct] = true;
          pushEvent('scroll_depth', { percent_scrolled: pct });
          updateEngagementScore();
        }
      });

      if (fired[90]) {
        window.removeEventListener('scroll', onScroll);
      }
    }

    var ticking = false;
    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(function () {
          checkScroll();
          ticking = false;
        });
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    checkScroll();
  }

  // Time on Page (active time via visibilitychange)
  function initTimeOnPage() {
    var milestones = [10, 30, 60, 180, 300];
    var elapsed = 0;
    var lastTick = Date.now();
    var active = !document.hidden;

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        elapsed += (Date.now() - lastTick) / 1000;
        active = false;
      } else {
        lastTick = Date.now();
        active = true;
      }
    });

    setInterval(function () {
      if (active) {
        elapsed += (Date.now() - lastTick) / 1000;
        lastTick = Date.now();
      }
      milestones.forEach(function (ms) {
        if (elapsed >= ms && !engagementState.timeMilestones[ms]) {
          engagementState.timeMilestones[ms] = true;
          pushEvent('time_on_page', { engagement_time_seconds: ms });
          updateEngagementScore();
        }
      });
    }, 1000);
  }

  // Product Image Interaction
  function initProductImageTracking() {
    if (DL.template !== 'product') return;

    var swiperEl = document.querySelector('.product__media-list .swiper, .product__media-list');
    if (!swiperEl) return;

    // Watch for swiper instance
    function attachSwiper() {
      if (swiperEl.swiper) {
        swiperEl.swiper.on('slideChange', function () {
          var idx = swiperEl.swiper.activeIndex;
          engagementState.imagesViewed[idx] = true;
          var count = Object.keys(engagementState.imagesViewed).length;
          var total = swiperEl.swiper.slides ? swiperEl.swiper.slides.length : 0;
          pushEvent('product_image_view', { image_index: idx });
          pushEvent('product_media_engagement', {
            images_viewed: count,
            total_images: total,
          });
          updateEngagementScore();
        });
        // First image counts
        engagementState.imagesViewed[0] = true;
      } else {
        setTimeout(attachSwiper, 500);
      }
    }
    attachSwiper();
  }

  // Accordion / Details
  function initAccordionTracking() {
    if (DL.template !== 'product') return;

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.collapsible-content__toggle, .product-comparison__desk-toggle, .accordion__button, details summary');
      if (!btn) return;
      var section;
      if (btn.classList.contains('product-comparison__desk-toggle')) {
        section = 'Product Comparison';
      } else if (btn.classList.contains('collapsible-content__toggle')) {
        var question = btn.querySelector('.collapsible-content__question');
        section = question ? question.textContent.trim() : btn.textContent.trim();
      } else {
        section = btn.textContent.trim();
      }
      if (engagementState.accordionsOpened[section]) return;
      engagementState.accordionsOpened[section] = true;
      pushEvent('product_detail_view', { detail_section: section });
      updateEngagementScore();
    });
  }

  // Engagement Score
  function updateEngagementScore() {
    var score = 0;

    // Scroll: 25%=5, 50%=10, 75%=15, 90%=20
    var scrollPts = { 25: 5, 50: 10, 75: 15, 90: 20 };
    Object.keys(engagementState.scrollThresholds).forEach(function (k) {
      score += scrollPts[k] || 0;
    });

    // Time: 10s=5, 30s=10, 60s=15, 180s=20, 300s=25
    var timePts = { 10: 5, 30: 10, 60: 15, 180: 20, 300: 25 };
    Object.keys(engagementState.timeMilestones).forEach(function (k) {
      score += timePts[k] || 0;
    });

    // Images: 5 per image, max 20
    var imgCount = Object.keys(engagementState.imagesViewed).length;
    score += Math.min(imgCount * 5, 20);

    // Accordions: 5 per section, max 15
    var accCount = Object.keys(engagementState.accordionsOpened).length;
    score += Math.min(accCount * 5, 15);

    // Variant: 10
    if (engagementState.variantChanged) score += 10;

    // Add to cart: 10
    if (engagementState.addedToCart) score += 10;

    score = Math.min(score, 100);

    if (score !== engagementState.score) {
      engagementState.score = score;
      debouncedScorePush();
    }
  }

  var debouncedScorePush = debounce(function () {
    pushEvent('engagement_score', {
      engagement_score: engagementState.score,
    });
  }, 5000);

  // Product Interest Escalation
  function trackProductInterest(product) {
    if (!product || !product.product_id) return;
    var pid = String(product.product_id);

    // Session
    var sessionKey = '_esel_session_products';
    var sessionProducts = [];
    try {
      sessionProducts = JSON.parse(sessionStorage.getItem(sessionKey) || '[]');
    } catch (e) {}
    var returningSession = sessionProducts.indexOf(pid) !== -1;
    if (!returningSession) {
      sessionProducts.push(pid);
      try { sessionStorage.setItem(sessionKey, JSON.stringify(sessionProducts)); } catch (e) {}
    }

    // Cross-session
    var storageKey = '_esel_all_products';
    var allProducts = [];
    try {
      allProducts = JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (e) {}
    var returningAll = allProducts.indexOf(pid) !== -1;
    if (!returningAll) {
      allProducts.push(pid);
      try { localStorage.setItem(storageKey, JSON.stringify(allProducts)); } catch (e) {}
    }

    pushEvent('user_interest_level', {
      session_products_viewed: sessionProducts.length,
      total_products_viewed: allProducts.length,
      returning_product_view: returningAll,
    });
  }

  // Cart Abandonment Signal
  function initCartAbandonmentTracking() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    var abandonTimer = null;

    var observer = new MutationObserver(function () {
      if (cartDrawer.classList.contains('active')) {
        pushEvent('cart_drawer_open');

        clearTimeout(abandonTimer);
        abandonTimer = setTimeout(function () {
          // Check if checkout was not started
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              if (cart.item_count > 0) {
                pushEvent('cart_abandonment_signal', {
                  cart_value: cart.total_price / 100,
                  cart_item_count: cart.item_count,
                });
              }
            });
        }, 30000);
      } else {
        clearTimeout(abandonTimer);
      }
    });

    observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }

  // ─── Init ──────────────────────────────────────────────────────────

  // Run immediately (before DOM ready)
  initConsent();
  initClickIds();
  initVisitorIdentity();

  // Run on DOM ready
  document.addEventListener('DOMContentLoaded', function () {
    firePageEvents();
    initInteractionEvents();
    initVariantChange();
    initScrollDepth();
    initTimeOnPage();
    initProductImageTracking();
    initAccordionTracking();
    initCartAbandonmentTracking();
  });
})();
